import React, { useState, useEffect } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography,
    Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/system";
import axios from "axios";

// Light-themed container (keeping the "NasaContainer" name, but changed to light styling)
const NasaContainer = styled(Box)({
    backgroundColor: "#ecf7fe", // Very light blue to match your other updated pages
    color: "#333",
    minHeight: "100vh",
    padding: "20px",
    boxSizing: "border-box",
    borderRadius: "12px",
    boxShadow: "0 0 20px rgba(25, 118, 210, 0.1)", // Subtle glow
});

// API endpoints
const DISTRICTS_URL = "https://api.swancreate.com/api/llm/SchoolDistrict";
const MEALVIEWER_SETUP_URL = "https://api.swancreate.com/api/llm/MealViewer_MenuSetup";

const MealViewerConfigurator = () => {
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [districts, setDistricts] = useState([]);
    const [menuSetupData, setMenuSetupData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch all districts
    useEffect(() => {
        const fetchDistricts = async () => {
            setLoading(true);
            try {
                const response = await axios.get(DISTRICTS_URL);
                const sorted = response.data.sort((a, b) =>
                    a.DistrictName.localeCompare(b.DistrictName)
                );
                setDistricts(sorted);
            } catch (error) {
                console.error("Error fetching districts:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDistricts();
    }, []);

    // Fetch all MealViewer_MenuSetup data on load
    useEffect(() => {
        const fetchMealViewerSetup = async () => {
            setLoading(true);
            try {
                const response = await axios.get(MEALVIEWER_SETUP_URL);
                setMenuSetupData(response.data);
                setFilteredData(response.data);
            } catch (error) {
                console.error("Error fetching MealViewer_MenuSetup:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchMealViewerSetup();
    }, []);

    // Filter data based on selected district
    useEffect(() => {
        if (selectedDistrict) {
            const filtered = menuSetupData.filter(
                (item) => item.DistrictID === selectedDistrict
            );
            setFilteredData(filtered);
        } else {
            setFilteredData(menuSetupData);
        }
    }, [selectedDistrict, menuSetupData]);

    // DataGrid columns
    const columns = [
        { field: "apiid", headerName: "API ID", flex: 1 },
        { field: "DistrictID", headerName: "District ID", flex: 1 },
        { field: "DistrictName", headerName: "District Name", flex: 1.2 },
        { field: "SiteID", headerName: "Site ID", flex: 1 },
        { field: "SchoolName", headerName: "School Name", flex: 1.2 },
        {
            field: "MealviewerSchoolName",
            headerName: "MealViewer School Name",
            flex: 1.2,
        },
        { field: "MenuUrl", headerName: "Menu URL", flex: 1.5 },
        { field: "api_link_base", headerName: "API Link Base", flex: 1.2 },
    ];

    // Prepare rows for DataGrid
    const rows = filteredData.map((item) => ({
        id: item.apiid,
        ...item,
    }));

    // Open the AI menu page in a new window
    const openAIMenu = (row) => {
        if (row.MenuUrl) {
            const menuUrl = encodeURIComponent(row.MenuUrl);
            window.open(
                `https://ai.swancreate.com/ai-menu?url=${menuUrl}&apiid=${row.apiid}`,
                "_blank",
                "width=1200,height=800,scrollbars=yes,resizable=yes"
            );
        } else {
            alert("MenuUrl is not available for this record.");
        }
    };

    // Open the form in a new window
    const openFormWindow = () => {
        window.open(
            "https://swanoffice.us:8443/lfserver/MealView_MenuSetup",
            "MealViewerSetup",
            "width=1200,height=800,left=200,top=100"
        );
    };

    return (
        <NasaContainer>
            {/* Header Row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: "#333" }}>
                    MealViewer Configurator
                </Typography>
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        fontWeight: "bold",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#1565c0" },
                    }}
                    onClick={openFormWindow}
                >
                    Open Setup Form
                </Button>
            </Box>

            {/* District Selector */}
            <Box mb={4} width="300px">
                <FormControl fullWidth>
                    <InputLabel sx={{ color: "#333" }}>Select District</InputLabel>
                    <Select
                        value={selectedDistrict}
                        onChange={(e) => setSelectedDistrict(e.target.value)}
                        sx={{
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ccc",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#999",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1976d2",
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    backgroundColor: "#fff",
                                    color: "#333",
                                },
                            },
                        }}
                    >
                        <MenuItem key="none-dist" value="">
                            -- Choose District --
                        </MenuItem>
                        {districts.map((dist) => (
                            <MenuItem key={dist.SchoolDistrictID} value={dist.SchoolDistrictID}>
                                {dist.DistrictName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Data Grid */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
                    <CircularProgress sx={{ color: "#1976d2" }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        height: 600,
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        border: "1px solid #ddd",
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: "#f9f9f9",
                        },
                        "& .MuiDataGrid-cell": { color: "#333" },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f5f5f5",
                            color: "#333",
                            borderBottom: "1px solid #ddd",
                            fontWeight: "600",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: "#f5f5f5",
                            color: "#333",
                            borderTop: "1px solid #ddd",
                        },
                    }}
                >
                    <DataGrid
                        rows={rows}
                        columns={[
                            ...columns,
                            {
                                field: "actions",
                                headerName: "Actions",
                                flex: 1,
                                renderCell: (params) => (
                                    <Button
                                        variant="contained"
                                        onClick={() => openAIMenu(params.row)}
                                        sx={{
                                            backgroundColor: "#1976d2",
                                            color: "#fff",
                                            textTransform: "none",
                                            "&:hover": { backgroundColor: "#1565c0" },
                                        }}
                                    >
                                        Open AI Menu
                                    </Button>
                                ),
                            },
                        ]}
                        pageSize={10}
                        disableSelectionOnClick
                    />
                </Box>
            )}
        </NasaContainer>
    );
};

export default MealViewerConfigurator;
